import Box from '@/common/components/Box'
import { ImageCarousel } from '@/common/components/ImageCarousel'
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder'
import ResumeItem from '@/common/components/ResumeItem'
import { formatUnit } from '@/common/utils/formatters'
import { DiagnosticPieceData } from '@/features/maintenance/features/workflows/types/forwardings/directiveEntries/diagnosticEntry/diagnosticEntry.type'
import { WrenchIcon } from 'lucide-react'
import EditProposalModal from '../../EditProposalModal'
import ProposalAwareValuesRow from '../../ProposalAwareValuesRow'
import { useProposalModification } from '../../useProposalModification'

interface PieceRowProps {
  executionPiece: DiagnosticPieceData
  fieldIndex: number
}

const PieceRow = ({ executionPiece, fieldIndex }: PieceRowProps) => {
  const modification = useProposalModification({
    fieldName: `pieces.${fieldIndex}`,
    unitPrice: executionPiece.unit_price,
    warrantyInDays: executionPiece.warranty_in_days,
    quantity: executionPiece.quantity,
    totalValue: executionPiece.total_value,
  })

  const quantityDisplay = (
    <ResumeItem className="text-sm" label="Qntd" value={formatUnit(executionPiece.quantity, 'item')} />
  )

  return (
    <Box className="p-4">
      <div className="flex w-full items-start gap-4">
        <ImageCarousel
          files={executionPiece.piece?.images?.map((image) => ({ id: image?.url, file: image?.url })) || []}
          Placeholder={
            <ImageWithPlaceholder className="size-28 rounded-md" iconClassName="size-10" placeholderIcon={WrenchIcon} />
          }
          className="size-28"
          showExpandButton
          showRemoveButton={false}
        />

        <div className="flex flex-col gap-2 flex-1">
          <p className="font-bold -mb-2">{executionPiece?.piece?.display_name || '--'}</p>

          <div className="flex flex-col gap-3">
            <ProposalAwareValuesRow
              originalUnitPrice={modification.originalUnitPrice}
              displayNewUnitPrice={modification.displayNewUnitPrice}
              unitPriceLabel="Valor unitário"
              unitPricePercentage={modification.unitPricePercentage}
              originalTotalValue={modification.originalTotalValue}
              displayNewTotalValue={modification.displayNewTotalValue}
              totalValuePercentage={modification.totalValuePercentage}
              originalWarranty={modification.originalWarranty}
              displayNewWarranty={modification.displayNewWarranty}
              quantityDisplay={quantityDisplay}
            />

            <div className="flex gap-4">
              <ResumeItem className="text-sm" label="Código da peça" value={executionPiece?.piece?.number || '--'} />

              <ResumeItem
                className="text-sm"
                label="Fabricante"
                value={executionPiece?.piece?.brand?.display_name || '--'}
              />

              <ResumeItem className="text-sm flex-1" label="Descrição" value={executionPiece.description} />
            </div>
          </div>
        </div>

        <EditProposalModal
          title={`Editar ${executionPiece?.piece?.display_name || '--'}`}
          isOpen={modification.isOpen}
          setIsOpen={modification.handleOpenChange}
          onConfirm={modification.onConfirm}
          localUnitPrice={modification.localUnitPrice}
          setLocalUnitPrice={modification.setLocalUnitPrice}
          localWarranty={modification.localWarranty}
          setLocalWarranty={modification.setLocalWarranty}
          originalUnitPrice={modification.originalUnitPrice}
          originalTotalValue={modification.originalTotalValue}
          originalWarranty={modification.originalWarranty}
          previewTotalValue={modification.previewTotalValue}
          unitPriceLabel="Valor unitário"
          quantityDisplay={quantityDisplay}
          disabled={modification.disabled}
        />
      </div>
    </Box>
  )
}

export default PieceRow
